import { useMutation, useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import {
  getDoctorAvailability,
  getDoctors,
  getFacilities,
  getFacilityById,
  getSpecializations,
  getUserSpecialization,
  createAppointment,
  getAppointmentById,
  getAppointmentsByPhoneNumber,
  updateAppointment,
  cancelAppointment,
} from "../API/appointmentApiService";
import { GetUserParams } from "../types/appointmentType";
import { getFacilityScope, isOnFacilityEntry } from "../utils/facilityScope";

// When this visit is scoped to a hospital (partner slug / embed), fetch ONLY that
// facility by id — so an embedded page never pulls the full hospital list.
export const useScopedFacility = () => {
  const scopeId = getFacilityScope()?.facilityId;
  const { data } = useQuery({
    queryKey: ["scoped-facility", scopeId],
    queryFn: () => getFacilityById(scopeId as number),
    enabled: !!scopeId,
    staleTime: 5 * 60 * 1000, // dedupe across components; the facility rarely changes mid-visit
  });
  return scopeId ? data?.item ?? null : null;
};
export const useGetSpecializations = () => {
  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ["specializations"],
    queryFn: () => getSpecializations(),
  });
  return { data, isLoading, isError, refetch };
};

// Specializations scoped to the facility: when scoped, show only the specializations
// the scoped hospital's doctors actually have (derived from their inline
// userSpecializations). No scope → the full list, unchanged.
export const useScopedSpecializations = () => {
  const scopeId = getFacilityScope()?.facilityId;
  const { data: all, isLoading, isError, refetch } = useGetSpecializations();
  const { data: doctorsData } = useQuery({
    queryKey: ["scoped-doctors-for-specs", scopeId],
    queryFn: () =>
      getDoctors({ page: 1, count: 100000, status: 1, userCategoryId: 2, facilityId: scopeId }),
    enabled: !!scopeId,
  });

  const data = useMemo(() => {
    if (!scopeId || !Array.isArray(all)) return all;
    const offered = new Set<number>();
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const items = (doctorsData as any)?.items ?? [];
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    items.forEach((d: any) =>
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (d?.userSpecializations ?? []).forEach((s: any) => {
        if (s?.specializationId != null) offered.add(Number(s.specializationId));
      })
    );
    // Until doctors load (or if none carry specializations) keep the full list
    // rather than hiding everything.
    if (offered.size === 0) return all;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return all.filter((s: any) => s && s.id != null && offered.has(Number(s.id)));
  }, [scopeId, all, doctorsData]);

  return { data, isLoading, isError, refetch };
};
// When scoped (partner slug / embed) this returns ONLY the scoped facility, and
// skips fetching the full hospital list — so no embedded page ever pulls every
// hospital. With no scope it behaves exactly as before (full list).
export const useGetFacilities = () => {
  const scopeId = getFacilityScope()?.facilityId;
  const scoped = useScopedFacility();
  const { data, isLoading, isError } = useQuery({
    queryKey: ["facilities"],
    queryFn: () => getFacilities(),
    // Skip the full list when scoped, or while a scope is about to resolve on the
    // facility-entry route (closes the transient window before scope is set).
    enabled: !scopeId && !isOnFacilityEntry(),
  });
  if (scopeId) {
    return {
      facilities: scoped ? [scoped] : [],
      isLoading: !scoped,
      isError: false,
    };
  }
  return { facilities: data, isLoading, isError };
};
export const useGetDoctors = ({
  page = 1,
  count = 100000,
  status = 1,
  userCategoryId = 2,
  facilityId,
}: GetUserParams) => {
  const { data, isLoading, isError } = useQuery({
    queryKey: ["doctors", facilityId],
    queryFn: () =>
      getDoctors({ page, count, status, userCategoryId, facilityId }),
  });
  return { doctors: data, isLoading, isError };
};
export const useGetDoctorAvailability = (
  userId: string,
  startDate?: Date | string | undefined,
  endDate?: Date | string | undefined
) => {
  // Convert string dates to Date objects if needed
  const startDateObj =
    startDate && typeof startDate === "string"
      ? new Date(startDate)
      : startDate;
  const endDateObj =
    endDate && typeof endDate === "string" ? new Date(endDate) : endDate;

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: [
      "doctorAvailability",
      userId,
      startDateObj instanceof Date ? startDateObj.toISOString() : undefined,
      endDateObj instanceof Date ? endDateObj.toISOString() : undefined,
    ],
    queryFn: () =>
      getDoctorAvailability(
        userId,
        startDateObj as Date | undefined,
        endDateObj as Date | undefined
      ),
    retry: 1,
  });
  return { timeSlots: data, isLoading, isError, refetch };
};
export const useGetUserSpecializations = (userId: number) => {
  const { data, isLoading, isError } = useQuery({
    queryKey: ["specializations"],
    queryFn: () => getUserSpecialization(userId),
  });
  return { doctorSpecializations: data, isLoading, isError };
};
export const useCreateAppointment = () => {
  const { mutateAsync, isPending, isSuccess } = useMutation({
    mutationFn: createAppointment,
    mutationKey: ["createAppointment"],
  });
  return { createAppointment: mutateAsync, isPending, isSuccess };
};

export const useGetAppointmentById = (id: number) => {
  return useQuery({
    queryKey: ["appointment", id],
    queryFn: () => getAppointmentById(id),
    enabled: !!id, // Only run if we have an ID
    retry: 1,
  });
};

export const useGetAppointmentsByPhoneNumber = (
  phoneNumber: string,
  page: number,
  count: number,
  status: number
) => {
  const { data, isLoading, isError } = useQuery({
    queryKey: ["appointments", phoneNumber, page, count, status],
    queryFn: () =>
      getAppointmentsByPhoneNumber(phoneNumber, page, count, status),
  });
  return { appointments: data, isLoading, isError };
};

export const useUpdateAppointment = () => {
  const { mutateAsync, isPending, isSuccess } = useMutation({
    mutationFn: updateAppointment,
    mutationKey: ["updateAppointment"],
  });
  return { updateAppointment: mutateAsync, isPending, isSuccess };
};

export const useCancelAppointment = () => {
  const { mutateAsync, isPending, isSuccess } = useMutation({
    mutationFn: cancelAppointment,
    mutationKey: ["cancelAppointment"],
  });
  return { cancelAppointment: mutateAsync, isPending, isSuccess };
};
